import React, { Component } from "react";
import { classNames, DomHandler, ObjectUtils, ZIndexUtils } from "primereact/utils";
import PrimeReact from "primereact/api";
import { Portal } from "./Portal";
import { CSSTransition } from "react-transition-group";
import Loader from "../Loader";
import { Download, RefreshCw, Undo2, ZoomOut, ZoomIn, X, Eye, ImageOff } from "lucide-react";

export interface ImageProps {
    preview?: boolean;
    className?: string;
    downloadable?: boolean;
    style?: string;
    imageStyle?: string;
    imageClassName?: string;
    template?: any;
    src?: string;
    alt?: string;
    width?: string;
    height?: string;
    onShow?(): void;
    onHide?(): void;
    onPreview: () => Promise<string>;
}

export interface ImageState {
    rotate: number;
    maskVisible: boolean;
    previewVisible: boolean;
    scale: number;
    source?: string;
    isLoading: boolean;
}

export class ExtendedImage extends Component<ImageProps, ImageState> {
    previewRef: React.RefObject<any>;
    previewClick: boolean | undefined;
    mask: HTMLDivElement | null = null;
    container: HTMLSpanElement | null = null;

    constructor(props: ImageProps | Readonly<ImageProps>) {
        super(props);
        this.state = {
            maskVisible: false,
            previewVisible: false,
            rotate: 0,
            scale: 1,
            source: "",
            isLoading: false,
        };

        this.onImageClick = this.onImageClick.bind(this);
        this.onMaskClick = this.onMaskClick.bind(this);
        this.onDownload = this.onDownload.bind(this);
        this.rotateRight = this.rotateRight.bind(this);
        this.rotateLeft = this.rotateLeft.bind(this);
        this.zoomIn = this.zoomIn.bind(this);
        this.zoomOut = this.zoomOut.bind(this);
        this.onEntering = this.onEntering.bind(this);
        this.onEntered = this.onEntered.bind(this);
        this.onPreviewImageClick = this.onPreviewImageClick.bind(this);
        this.onExit = this.onExit.bind(this);
        this.onExiting = this.onExiting.bind(this);
        this.onExited = this.onExited.bind(this);

        this.previewRef = React.createRef();
    }

    onImageClick = async () => {
        if (this.props.preview) {
            this.setState({ maskVisible: true });
            setTimeout(() => {
                this.setState({ previewVisible: true });
            }, 25);
        }
    };

    onPreviewImageClick = () => {
        this.previewClick = true;
    };

    onMaskClick = () => {
        if (!this.previewClick) {
            this.setState({ previewVisible: false });
            this.setState({ rotate: 0 });
            this.setState({ scale: 1 });
        }

        this.previewClick = false;
    };

    onDownload = () => {
        const { alt: name } = this.props;
        const source = this.state.source as any;
        DomHandler.saveAs({ name: name ?? "", url: source });
        this.previewClick = true;
    };

    rotateRight = () => {
        this.setState((prevState) => ({
            rotate: prevState.rotate + 90,
        }));

        this.previewClick = true;
    };

    rotateLeft = () => {
        this.setState((prevState) => ({
            rotate: prevState.rotate - 90,
        }));

        this.previewClick = true;
    };

    zoomIn = () => {
        this.setState((prevState) => ({
            scale: prevState.scale + 0.1,
        }));

        this.previewClick = true;
    };

    zoomOut = () => {
        this.setState((prevState) => ({
            scale: prevState.scale - 0.1,
        }));

        this.previewClick = true;
    };

    onEntering = () => {
        (ZIndexUtils as any).set("modal", this.mask, PrimeReact.autoZIndex, PrimeReact.zIndex!["modal"]);
    };

    onEntered = () => {
        if (this.props.onShow) {
            this.props.onShow();
        }
    };

    onExit = () => {
        if (this.mask) this.mask.style.opacity = "0";
    };

    onExiting = () => {
        if (this.props.onHide) {
            this.props.onHide();
        }
    };

    onExited = (el: HTMLElement) => {
        ZIndexUtils.clear(el);

        this.setState({ maskVisible: false });
    };

    hidePreview = () => {};

    componentWillUnmount = () => {
        if (this.mask) {
            ZIndexUtils.clear(this.container!);
        }
    };

    runOnPreviewCallBack = async () => {
        this.setState({ isLoading: true });

        const source = await this.props.onPreview();

        this.setState({ isLoading: false, source: source });
    };

    loadImage = () => {
        if (this.state.isLoading) {
            return <Loader />;
        }

        if (this.state.source == null || this.state.source.length == 0) {
            this.runOnPreviewCallBack();
            return "";
        }

        const imagePreviewStyle = { transform: "rotate(" + this.state.rotate + "deg) scale(" + this.state.scale + ")" };

        return (
            <img
                src={this.state.source}
                className="max-w-[80vw] max-h-[80vh] object-contain"
                style={imagePreviewStyle}
                onClick={this.onPreviewImageClick}
                alt={this.props.alt}
            />
        );
    };

    renderElement = () => {
        const { downloadable } = this.props;
        const zoomDisabled = this.state.scale <= 0.5 || this.state.scale >= 1.5;
        // const rotateClassName = 'p-image-preview-rotate-' + this.state.rotate;

        return (
            <div ref={(el) => (this.mask = el)} className="fixed inset-0 flex items-center justify-center bg-black/70 z-[9999]" onClick={this.onMaskClick}>
                <div className="absolute top-4 right-4 flex items-center gap-2 z-[10000]">
                    {downloadable && (
                        <button className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-transparent border-0 text-white/80 hover:text-white hover:bg-white/20 transition-colors cursor-pointer" onClick={this.onDownload} type="button">
                            <Download size={20} />
                        </button>
                    )}
                    <button className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-transparent border-0 text-white/80 hover:text-white hover:bg-white/20 transition-colors cursor-pointer" onClick={this.rotateRight} type="button">
                        <RefreshCw size={20} />
                    </button>
                    <button className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-transparent border-0 text-white/80 hover:text-white hover:bg-white/20 transition-colors cursor-pointer" onClick={this.rotateLeft} type="button">
                        <Undo2 size={20} />
                    </button>
                    <button className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-transparent border-0 text-white/80 hover:text-white hover:bg-white/20 transition-colors cursor-pointer disabled:opacity-40" onClick={this.zoomOut} type="button" disabled={zoomDisabled}>
                        <ZoomOut size={20} />
                    </button>
                    <button className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-transparent border-0 text-white/80 hover:text-white hover:bg-white/20 transition-colors cursor-pointer disabled:opacity-40" onClick={this.zoomIn} type="button" disabled={zoomDisabled}>
                        <ZoomIn size={20} />
                    </button>
                    <button className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-transparent border-0 text-white/80 hover:text-white hover:bg-white/20 transition-colors cursor-pointer" type="button" onClick={this.hidePreview}>
                        <X size={20} />
                    </button>
                </div>
                <CSSTransition
                    nodeRef={this.previewRef}
                    classNames="p-image-preview"
                    in={this.state.previewVisible}
                    timeout={{ enter: 150, exit: 150 }}
                    unmountOnExit
                    onEntering={this.onEntering}
                    onEntered={this.onEntered}
                    onExit={this.onExit}
                    onExiting={this.onExiting}
                    onExited={this.onExited}
                >
                    <div ref={this.previewRef}>{this.loadImage()}</div>
                </CSSTransition>
            </div>
        );
    };

    render() {
        const containerClassName = classNames("relative inline-block", this.props.className);
        const content = this.props.template ? (
            ObjectUtils.getJSXElement(this.props.template, this.props)
        ) : (
            <Eye size={20} className="text-white" />
        );

        const { alt, width, height } = this.props;

        return (
            <span ref={(el) => (this.container = el)} className={containerClassName} style={this.props.style as any}>
                {this.props.src ? (
                    <img
                        src={this.props.src}
                        className={this.props.imageClassName}
                        width={width}
                        height={height}
                        style={{ display: "block", ...this.props.imageStyle as any }}
                        alt={alt}
                    />
                ) : (
                    <div
                        className={`flex items-center justify-center bg-neutral-100 dark:bg-neutral-800 rounded-lg ${this.props.imageClassName || ""}`}
                        style={{ width: width || "100%", height: height || "auto", minHeight: "40px", display: "block", ...this.props.imageStyle as any }}
                    >
                        <ImageOff size={20} strokeWidth={1.5} className="text-neutral-400 dark:text-neutral-500" />
                    </div>
                )}
                {this.props.preview && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 hover:opacity-100 transition-opacity cursor-pointer" onClick={this.onImageClick}>
                        {content}
                    </div>
                )}

                {this.state.maskVisible && <Portal element={this.renderElement()} appendTo={document.body} />}
            </span>
        );
    }
}
